import { lazy } from "react";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext.jsx";
import ProtectedRoute, {
  PublicOnlyRoute,
  RequireRole,
} from "./components/ProtectedRoute.jsx";
import AppLayout from "./components/layout/AppLayout.jsx";
import RouteFallback from "./components/RouteFallback.jsx";
import ChunkErrorBoundary from "./components/ChunkErrorBoundary.jsx";

/**
 * Every screen is code-split, so the entry chunk carries only React, the router,
 * Axios and the app shell. First paint no longer waits on the reports, payments
 * and employee views.
 */
const lazyWithRetry = (loader) => () =>
  loader().catch((error) => loader().catch(() => {
    throw error;
  }));

const LoginPage = lazy(lazyWithRetry(() => import("./pages/LoginPage.jsx")));
const DashboardPage = lazy(lazyWithRetry(() => import("./pages/DashboardPage.jsx")));
const QuickSalesPage = lazy(lazyWithRetry(() => import("./pages/QuickSalesPage.jsx")));
const ClientsPage = lazy(lazyWithRetry(() => import("./pages/ClientsPage.jsx")));
const ClientDetailPage = lazy(lazyWithRetry(() => import("./pages/ClientDetailPage.jsx")));
const ClientFormPage = lazy(lazyWithRetry(() => import("./pages/ClientFormPage.jsx")));
const AppointmentsPage = lazy(lazyWithRetry(() => import("./pages/AppointmentsPage.jsx")));
const AppointmentFormPage = lazy(lazyWithRetry(() => import("./pages/AppointmentFormPage.jsx")));
const EmployeesPage = lazy(lazyWithRetry(() => import("./pages/EmployeesPage.jsx")));
const StaffManagementPage = lazy(lazyWithRetry(() => import("./pages/StaffManagementPage.jsx")));
const StaffAttendancePage = lazy(lazyWithRetry(() => import("./pages/StaffAttendancePage.jsx")));
const ServicesPage = lazy(lazyWithRetry(() => import("./pages/ServicesPage.jsx")));
const InventoryPage = lazy(lazyWithRetry(() => import("./pages/InventoryPage.jsx")));
const PaymentsPage = lazy(lazyWithRetry(() => import("./pages/PaymentsPage.jsx")));
const ExpensesPage = lazy(lazyWithRetry(() => import("./pages/ExpensesPage.jsx")));
const ReportsPage = lazy(lazyWithRetry(() => import("./pages/ReportsPage.jsx")));
const ProfilePage = lazy(lazyWithRetry(() => import("./pages/ProfilePage.jsx")));
const NotFoundPage = lazy(lazyWithRetry(() => import("./pages/NotFoundPage.jsx")));

// Public website pages
const PublicLayout = lazy(lazyWithRetry(() => import("./components/public/PublicLayout.jsx")));
const HomePage = lazy(lazyWithRetry(() => import("./pages/public/HomePage.jsx")));
const AboutPage = lazy(lazyWithRetry(() => import("./pages/public/AboutPage.jsx")));
const PublicServicesPage = lazy(lazyWithRetry(() => import("./pages/public/ServicesPage.jsx")));
const ServiceDetailPage = lazy(lazyWithRetry(() => import("./pages/public/ServiceDetailPage.jsx")));
const ShopPage = lazy(lazyWithRetry(() => import("./pages/public/ShopPage.jsx")));
const ProductDetailPage = lazy(lazyWithRetry(() => import("./pages/public/ProductDetailPage.jsx")));
const ContactPage = lazy(lazyWithRetry(() => import("./pages/public/ContactPage.jsx")));
const BookingPage = lazy(lazyWithRetry(() => import("./pages/public/BookingPage.jsx")));
const PublicNotFoundPage = lazy(
  lazyWithRetry(() => import("./pages/public/PublicNotFoundPage.jsx")),
);

/** react-router already renders this Suspense boundary for us. */
const screen = (Component, props) => (
  <Component {...props} fallback={<RouteFallback />} />
);

/**
 * Admin-only screen. `RequireRole` is a second, finer gate inside the signed-in
 * shell — it keeps a staff login away from the financial modules and the staff
 * roster even if they type the URL, while `ProtectedRoute` above only answers
 * "are you signed in?".
 */
const adminScreen = (Component, props) => (
  <RequireRole roles={["admin"]}>{screen(Component, props)}</RequireRole>
);

const router = createBrowserRouter([
  // Public marketing site. Registered before the dashboard so the index route
  // at "/" wins; the dashboard shell below is pathless and mounts its children
  // (`/dashboard`, `/appointments`, `/services`, …) at the top level, exactly
  // where every sidebar/TopNavBar link already points.
  {
    path: "/",
    element: screen(PublicLayout),
    children: [
      { index: true, element: screen(HomePage) },
      { path: "about", element: screen(AboutPage) },
      { path: "services", element: screen(PublicServicesPage) },
      { path: "services/:slug", element: screen(ServiceDetailPage) },
      { path: "shop", element: screen(ShopPage) },
      { path: "shop/:slug", element: screen(ProductDetailPage) },
      { path: "contact", element: screen(ContactPage) },
      { path: "booking", element: screen(BookingPage) },
      { path: "*", element: screen(PublicNotFoundPage) },
    ],
  },
  {
    // Admin sign-in. `/login` is kept as a permanent redirect below so any
    // bookmark, emailed link or hardcoded reference keeps working.
    path: "/admin",
    element: (
      <PublicOnlyRoute>
        <LoginPage />
      </PublicOnlyRoute>
    ),
  },
  {
    // Legacy sign-in URL → the canonical `/admin` login route.
    path: "/login",
    element: <Navigate to="/admin" replace />,
  },
  {
    // Pathless layout route: the signed-in shell wraps the operational screens
    // without consuming a URL segment, so the existing routes are untouched.
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      { path: "dashboard", element: screen(DashboardPage) },
      { path: "quick-sales", element: screen(QuickSalesPage) },
      { path: "clients", element: screen(ClientsPage) },
      { path: "clients/new", element: screen(ClientFormPage, { mode: "create" }) },
      { path: "clients/:id", element: screen(ClientDetailPage) },
      { path: "clients/:id/edit", element: screen(ClientFormPage, { mode: "edit" }) },
      { path: "appointments", element: screen(AppointmentsPage) },
      { path: "appointments/new", element: screen(AppointmentFormPage, { mode: "create" }) },
      {
        path: "appointments/:id/edit",
        element: screen(AppointmentFormPage, { mode: "edit" }),
      },
      { path: "employees", element: adminScreen(EmployeesPage) },
      { path: "staff", element: adminScreen(StaffManagementPage) },
      { path: "my-attendance", element: screen(StaffAttendancePage) },
      { path: "profile", element: screen(ProfilePage) },
      {
        // Staff management of the service menu keeps the dashboard screen,
        // which lives at `/admin/services`; the public catalogue owns
        // `/services`. `ServicesPage` links internally stay under `/admin`.
        path: "admin/services",
        element: screen(ServicesPage),
      },
      { path: "inventory", element: screen(InventoryPage) },
      { path: "payments", element: adminScreen(PaymentsPage) },
      { path: "expenses", element: adminScreen(ExpensesPage) },
      { path: "reports", element: adminScreen(ReportsPage) },
      // Signed-in catch-all: an unknown authenticated URL renders the app's
      // dashboard-flavoured 404 rather than the public site's shell.
      { path: "*", element: screen(NotFoundPage) },
    ],
  },
]);

export default function App() {
  return (
    <AuthProvider>
      <ChunkErrorBoundary>
        <RouterProvider router={router} />
      </ChunkErrorBoundary>
    </AuthProvider>
  );
}
